import type { LocalAudioGradeEventDetail } from 'Utils/LocalAudioSessionEvents';
import type { RecognitionDetectedNote, RecognitionFrame } from '../types';
import type { SightReadingExpectedMidiSnapshot } from './sightReadingSession';

export type SightReadingLiveCandidate = {
  midiNote: number;
  confidence: number;
};

export type SightReadingLiveFrame = {
  expectedMidiNotes: number[];
  recognizedMidiNotes: number[];
  onsetMidiNotes: number[];
  candidates: SightReadingLiveCandidate[];
  rms: number;
  minRms: number;
  waveform: number[];
};

export type SightReadingLiveJudgment = LocalAudioGradeEventDetail & {
  id: string;
  recognizedMidiNotes: number[];
  candidateMidiNotes: number[];
};

const normalizeMidiNotes = (midiNotes: number[]) =>
  Array.from(new Set(midiNotes.map(Math.round)))
    .filter((midiNote) => midiNote >= 0 && midiNote <= 127)
    .sort((left, right) => left - right);

const candidateConfidence = (candidate: RecognitionDetectedNote) =>
  Math.max(0, Math.min(1, candidate.confidence ?? candidate.score ?? 0));

export const buildSightReadingLiveFrame = (
  frame: RecognitionFrame,
  expected: SightReadingExpectedMidiSnapshot,
): SightReadingLiveFrame => ({
  expectedMidiNotes: normalizeMidiNotes(expected.midiNotes),
  recognizedMidiNotes: normalizeMidiNotes(frame.midiNotes ?? []),
  onsetMidiNotes: normalizeMidiNotes(frame.onsetMidiNotes ?? []),
  candidates: (frame.detectedNotes ?? [])
    .map((candidate) => ({
      midiNote: Math.round(candidate.midi),
      confidence: candidateConfidence(candidate),
    }))
    .filter((candidate) => candidate.midiNote >= 0 && candidate.midiNote <= 127)
    .sort((left, right) => right.confidence - left.confidence)
    .slice(0, 4),
  rms: frame.rms ?? 0,
  minRms: frame.minRms ?? 0,
  waveform: Array.from(frame.waveform ?? []),
});

export const buildSightReadingLiveJudgment = (
  detail: LocalAudioGradeEventDetail,
  liveFrame: SightReadingLiveFrame | null,
): SightReadingLiveJudgment => ({
  ...detail,
  id: `${detail.capturedAtMs}-${detail.phase}-${detail.midiNote}-${detail.state}`,
  recognizedMidiNotes: liveFrame?.recognizedMidiNotes ?? [],
  candidateMidiNotes: normalizeMidiNotes(
    liveFrame?.candidates.map((candidate) => candidate.midiNote) ?? [],
  ),
});
